// hooks/useSettings.ts
import { useState, useEffect } from "react";
import { dataSource } from "@/lib/dataSource";
import { useToast } from "@/hooks/use-toast";

export interface AppSettings {
  canonicalizeCode: boolean;
  enforceUniqueNames: boolean;
  nlpProvider: string;
  autoSave: boolean;
  theme: string;
  apiEndpoint?: string;
}

export function useSettings() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const settingsData = await dataSource.getSettings();

      // Check if we have localStorage settings as fallback
      const localSettings = localStorage.getItem("app-settings");
      if (localSettings) {
        const parsedLocal = JSON.parse(localSettings);
        // Merge API settings with local settings (local takes precedence)
        setSettings({ ...settingsData, ...parsedLocal });
      } else {
        setSettings(settingsData);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      // Fallback to default settings
      setSettings({
        canonicalizeCode: true,
        enforceUniqueNames: true,
        nlpProvider: "LocalRules",
        autoSave: true,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<AppSettings>) => {
    if (!settings) return;

    const newSettings = { ...settings, ...updates };

    try {
      const savedSettings = await dataSource.updateSettings(newSettings);
      setSettings(savedSettings);

      toast({
        title: "Settings Updated",
        description: "Your changes have been saved.",
      });
    } catch (error) {
      console.error("Error updating settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    }
  };

  return {
    settings,
    isLoading,
    updateSettings,
    refreshSettings: loadSettings,
  };
}
